"use client";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useReducer } from "react";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import {
  flightSearchReducer,
  initialState,
} from "@/reducers/flightSearchReducer";
import { createFlightSearchSchema } from "@/schemas/flightSearchSchema";
import { flightService } from "@/services/flightService";
import { staticDataService } from "@/services/staticService";
import { formatDate, isBefore } from "@/utils/dateFormatter";

export const useFlightSearch = (initialData = {}) => {
  // Initial data ile state'i merge et
  const mergedInitialState = {
    ...initialState,
    ...initialData,
  };

  // Eğer initialMultiFlights varsa multiFlights'ı güncelle
  if (
    initialData.initialMultiFlights &&
    initialData.initialMultiFlights.length > 0
  ) {
    mergedInitialState.multiFlights = initialData.initialMultiFlights;

    // Debug: Multi-city initial data
    if (initialData.activeTab === 2) {
    }
  }

  const [state, dispatch] = useReducer(flightSearchReducer, mergedInitialState);
  const locale = useLocale();
  const { currency } = useCurrency();
  const tFlightSearch = useTranslationDetector("flightSearch");
  const flightSearchSchema = createFlightSearchSchema(tFlightSearch);

  const setLoading = (loading) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  };

  const setInitialLoading = (loading) => {
    dispatch({ type: "SET_INITIAL_LOADING", payload: loading });
  };

  const setOnlyDirectFlights = (onlyDirectFlights) => {
    dispatch({ type: "SET_ONLY_DIRECT_FLIGHTS", payload: onlyDirectFlights });
  };

  const setActiveTab = (activeTab) => {
    dispatch({ type: "SET_ACTIVE_TAB", payload: activeTab });
  };

  const setDepartureDate = (departureDate) => {
    dispatch({ type: "SET_DEPARTURE_DATE", payload: departureDate });
  };

  const setReturnDate = (returnDate) => {
    dispatch({ type: "SET_RETURN_DATE", payload: returnDate });
  };

  const setDepartureCity = useCallback((departureCity) => {
    dispatch({ type: "SET_DEPARTURE_CITY", payload: departureCity });
  }, []);

  const setReturnCity = useCallback((returnCity) => {
    dispatch({ type: "SET_RETURN_CITY", payload: returnCity });
  }, []);

  const toggleDepartureAndReturn = () => {
    dispatch({ type: "TOGGLE_DEPARTURE_AND_RETURN" });
  };

  const setPassengers = (passengers) => {
    dispatch({ type: "SET_PASSENGERS", payload: passengers });
  };

  const setCabinClass = (cabinClass) => {
    dispatch({ type: "SET_CABIN_CLASS", payload: cabinClass });
  };

  const setPassengersAndCabinClass = (data) => {
    dispatch({ type: "SET_PASSENGERS_AND_CABIN_CLASS", payload: data });
  };

  // Multi-city flight functions
  const addMultiFlight = () => {
    dispatch({ type: "ADD_MULTI_FLIGHT" });
  };

  const removeMultiFlight = (flightId) => {
    dispatch({ type: "REMOVE_MULTI_FLIGHT", payload: flightId });
  };

  const updateMultiFlight = (flightId, field, value) => {
    dispatch({
      type: "UPDATE_MULTI_FLIGHT",
      payload: { flightId, field, value },
    });
  };

  const toggleMultiFlightCities = (flightId) => {
    dispatch({ type: "TOGGLE_MULTI_FLIGHT_CITIES", payload: flightId });
  };

  const setLatestFlightSearches = (latestFlightSearches) => {
    dispatch({
      type: "SET_LATEST_FLIGHT_SEARCHES",
      payload: latestFlightSearches,
    });
  };

  const setOneWayCalendarPrices = (oneWayCalendarPrices) => {
    dispatch({
      type: "SET_ONE_WAY_CALENDAR_PRICES",
      payload: oneWayCalendarPrices,
    });
  };

  const setPopularCities = (popularCities) => {
    dispatch({ type: "SET_POPULAR_CITIES", payload: popularCities });
  };

  const setNearestAirports = (nearestAirports) => {
    dispatch({ type: "SET_NEAREST_AIRPORTS", payload: nearestAirports });
  };

  const setCheapestFlightsMonthly = (cheapestFlightsMonthly) => {
    dispatch({
      type: "SET_CHEAPEST_FLIGHTS_MONTHLY",
      payload: cheapestFlightsMonthly,
    });
  };

  const setCheapestFlightsMonthlyByMonth = (cheapestFlightsMonthlyByMonth) => {
    dispatch({
      type: "SET_CHEAPEST_FLIGHTS_MONTHLY_BY_MONTH",
      payload: cheapestFlightsMonthlyByMonth,
    });
  };

  const setPublicHolidays = (publicHolidays) => {
    dispatch({ type: "SET_PUBLIC_HOLIDAYS", payload: publicHolidays });
  };

  const getPopularCities = async () => {
    try {
      const popularCitiesResponse = await flightService.getPopularCities({
        LanguageCode: locale.toUpperCase(),
      });
      const popularCities =
        popularCitiesResponse?.PopularCities.length === 0
          ? null
          : popularCitiesResponse?.PopularCities.length > 10
            ? popularCitiesResponse?.PopularCities.slice(0, 10)
            : popularCitiesResponse?.PopularCities;
      setPopularCities(popularCities);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "getPopularCities: Popular cities yüklenirken hata oluştu:",
        error,
      );
    }
  };

  const getNearestAirports = async () => {
    try {
      const nearestAirportsResponse = await flightService.getNearestAirports({
        LanguageCode: locale.toUpperCase(),
      });
      const nearestAirports =
        nearestAirportsResponse?.NeighborCities.length === 0
          ? null
          : nearestAirportsResponse?.NeighborCities.length > 1
            ? nearestAirportsResponse?.NeighborCities.slice(0, 1)
            : nearestAirportsResponse?.NeighborCities;
      setNearestAirports(nearestAirports);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "getNearestAirports: Nearest airports yüklenirken hata oluştu:",
        error,
      );
    }
  };

  const getCheapestFlightsMonthlyByMonth = async (
    month,
    year,
    index,
    currentCheapestFlightsMonthly = null,
  ) => {
    try {
      // Ayın son gününü bul
      const lastDay = new Date(year, month, 0).getDate();

      // Bugünün tarihi
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`;

      // Ayın ilk günü
      const monthFirstDay = `${year}-${month.toString().padStart(2, "0")}-01`;

      // Eğer ayın ilk günü geçmişte ise bugünün tarihini kullan
      const startDate = monthFirstDay < todayStr ? todayStr : monthFirstDay;
      const endDate = `${year}-${month.toString().padStart(2, "0")}-${lastDay.toString().padStart(2, "0")}`;

      const cheapestFlightsMonthlyResponse =
        await flightService.getCheapestFlightsTo({
          Origins: [
            {
              Code: state.departureCity.code,
            },
          ],
          Destination: {
            Code: state.returnCity.code,
          },
          MaxNumberOfResults: 15,
          TripType: state.isRoundTrip ? "RT" : "OW",
          StartDate: startDate,
          EndDate: endDate,
          FetchSegmentDetails: true,
          Currency: currency.toUpperCase(),
          Language: locale.toUpperCase(),
          CallerId: "saka",
          RoundTripOptions: state.isRoundTrip
            ? {
                MinStay: 1,
                MaxStay: 10,
              }
            : null,
        });

      setCheapestFlightsMonthlyByMonth(
        cheapestFlightsMonthlyResponse.length === 0
          ? null
          : cheapestFlightsMonthlyResponse,
      );
      const cheapestFlightsMonthlyData =
        currentCheapestFlightsMonthly || state.cheapestFlightsMonthly;

      if (!cheapestFlightsMonthlyData) {
        // eslint-disable-next-line no-console
        console.error(
          "getCheapestFlightsMonthlyByMonth: cheapestFlightsMonthly verisi mevcut değil",
        );
        return;
      }

      const newCheapestFlightsMonthly = {
        ...cheapestFlightsMonthlyData,
        MonthlyFlightFares: cheapestFlightsMonthlyData.MonthlyFlightFares.map(
          (item, arrayIndex) => {
            return {
              ...item,
              selected: arrayIndex === index,
            };
          },
        ),
      };
      setCheapestFlightsMonthly(newCheapestFlightsMonthly);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "getCheapestFlightsMonthlyByMonth: Cheapest flights monthly yüklenirken hata oluştu:",
        error,
      );
    }
  };

  const getCheapestFlightsMonthly = useCallback(async () => {
    try {
      const cheapestFlightsMonthlyResponse =
        await flightService.getCheapestFlightsMonthly({
          Origin: {
            Code: state.departureCity.code,
          },
          Destination: {
            Code: state.returnCity.code,
          },
          Currency: currency.toUpperCase(),
        });

      const cheapestFlightsMonthly =
        !cheapestFlightsMonthlyResponse ||
        !cheapestFlightsMonthlyResponse.MonthlyFlightFares ||
        cheapestFlightsMonthlyResponse.MonthlyFlightFares.length === 0
          ? null
          : {
              ...cheapestFlightsMonthlyResponse,
              MonthlyFlightFares:
                cheapestFlightsMonthlyResponse.MonthlyFlightFares.sort(
                  (a, b) => {
                    // Önce yıla göre sıralama
                    if (a.Year !== b.Year) {
                      return a.Year - b.Year;
                    }
                    // Yıl aynıysa aya göre sıralama
                    return a.Month - b.Month;
                  },
                ),
            };
      setCheapestFlightsMonthly(cheapestFlightsMonthly);
      if (cheapestFlightsMonthly) {
        getCheapestFlightsMonthlyByMonth(
          cheapestFlightsMonthly.MonthlyFlightFares[0].Month,
          cheapestFlightsMonthly.MonthlyFlightFares[0].Year,
          0,
          cheapestFlightsMonthly,
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "getCheapestFlightsMonthly: Cheapest flights monthly yüklenirken hata oluştu:",
        error,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function markCheapestDays(daysArray) {
    const daysByMonth = {};

    daysArray.forEach((day, index) => {
      // Price 0 olanları ve olmayan itemları atla
      if (!day.Price || day.Price === 0 || !day.date) return;

      const date = new Date(day.date);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;

      if (!daysByMonth[monthKey]) {
        daysByMonth[monthKey] = [];
      }

      daysByMonth[monthKey].push({
        index,
        price: day.Price,
        date,
      });
    });

    const result = daysArray.map((day) => ({ ...day }));

    Object.values(daysByMonth).forEach((monthDays) => {
      monthDays
        .sort((a, b) => {
          // Önce fiyata göre sırala
          if (a.price !== b.price) {
            return a.price - b.price;
          }
          // Fiyat aynıysa tarihe göre sırala (en erken tarih önce)
          return a.date.getTime() - b.date.getTime();
        })
        .slice(0, 3)
        .forEach(({ index }) => {
          result[index].cheapest = true;
        });
    });

    return result;
  }

  const getOneWayCalendarPrices = async () => {
    try {
      const oneWayCalendarPricesResponse =
        await flightService.getOneWayCalendarPrices({
          Origin: state.departureCity.code,
          OriginIsCity: true,
          Destination: state.returnCity.code,
          DestinationIsCity: false,
          DepartureDay: formatDate(state.departureDate, {
            format: "YYYY-MM-DD",
          }),
          Currency: currency.toUpperCase(),
          Language: locale.toUpperCase(),
          NumberOfDays: 119,
        });

      // Response null değilse ve DailyPrices boş array değilse formatla
      if (
        oneWayCalendarPricesResponse &&
        oneWayCalendarPricesResponse.DailyPrices &&
        oneWayCalendarPricesResponse.DailyPrices.length > 0
      ) {
        const departureDate = new Date(state.departureDate);

        const formattedDailyPrices =
          oneWayCalendarPricesResponse.DailyPrices.map((price, index) => {
            return {
              ...price,
              date: new Date(
                departureDate.getTime() + index * 24 * 60 * 60 * 1000,
              ), // Her gün için 1 gün ekle
            };
          });
        const updatedArray = markCheapestDays(
          formattedDailyPrices,
          departureDate,
        );
        setOneWayCalendarPrices(updatedArray);
      } else {
        setOneWayCalendarPrices(null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "getOneWayCalendarPrices: One way calendar prices yüklenirken hata oluştu:",
        error,
      );
      setOneWayCalendarPrices(null);
    }
  };

  const getRoundTripCalendarPrices = async () => {
    try {
      const oneWayCalendarPricesResponse =
        await flightService.getRoundTripCalendarPrices({
          Origin: state.departureCity.code,
          OriginIsCity: true,
          Destination: state.returnCity.code,
          DestinationIsCity: false,
          DepartureDay: formatDate(state.departureDate, {
            format: "YYYY-MM-DD",
          }),
          ReturnDay: formatDate(state.departureDate, {
            format: "YYYY-MM-DD",
          }),
          Currency: currency.toUpperCase(),
          Language: locale.toUpperCase(),
          NumberOfDays: 119,
        });

      // Response null değilse ve DailyPrices boş array değilse formatla
      if (
        oneWayCalendarPricesResponse &&
        oneWayCalendarPricesResponse.TotalCalculated?.DailyPrices &&
        oneWayCalendarPricesResponse.TotalCalculated?.DailyPrices.length > 0
      ) {
        const departureDate = new Date(state.departureDate);
        const formattedDailyPrices =
          oneWayCalendarPricesResponse.TotalCalculated?.DailyPrices.map(
            (price, index) => ({
              ...price,
              date: new Date(
                departureDate.getTime() + index * 24 * 60 * 60 * 1000,
              ), // Her gün için 1 gün ekle
            }),
          );
        const updatedArray = markCheapestDays(
          formattedDailyPrices,
          departureDate,
        );

        setOneWayCalendarPrices(updatedArray);
      } else {
        setOneWayCalendarPrices(null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "getOneWayCalendarPrices: One way calendar prices yüklenirken hata oluştu:",
        error,
      );
      setOneWayCalendarPrices(null);
    }
  };

  // Search for flight suggestions
  const searchForFromCity = useCallback(
    async (query) => {
      if (query.length < 2) return;

      return await flightService.getFlightSuggestionsByQuery({
        term: query,
        languageCode: locale.toUpperCase(),
      });
    },
    [locale],
  );

  // Şehir kodundan şehir bilgisi getiren fonksiyon
  const getCityInfoByCode = useCallback(
    async (cityCode) => {
      try {
        const response = await flightService.getFlightSuggestionsByQuery({
          term: cityCode,
          languageCode: locale.toUpperCase(),
        });

        // Response'dan ilk uygun sonucu bul
        if (response && response.length > 0) {
          // Tam eşleşme ara (kod tam olarak uyuşanı bul)
          const exactMatch = response.find(
            (item) =>
              item.code && item.code.toUpperCase() === cityCode.toUpperCase(),
          );

          if (exactMatch) {
            return exactMatch;
          }

          // Tam eşleşme yoksa ilk sonucu döndür
          return response[0];
        }

        return null;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          `getCityInfoByCode: ${cityCode} için şehir bilgisi getirilemedi:`,
          error,
        );
        return null;
      }
    },
    [locale],
  );

  // City selection handlers
  const onSelectDepartureCity = useCallback(
    (airport) => {
      // Tarih kontrolü
      if (airport?.departureDateRaw || airport?.returnDateRaw) {
        const now = new Date();

        if (
          airport.departureDateRaw &&
          !isBefore(airport.departureDateRaw, now)
        ) {
          setDepartureDate(new Date(airport.departureDateRaw));
        }

        if (airport.returnDateRaw && !isBefore(airport.returnDateRaw, now)) {
          setReturnDate(new Date(airport.returnDateRaw));
        }
      }

      if (airport?.departureCity) {
        setDepartureCity(airport.departureCity);
        if (airport.returnCity) {
          setReturnCity(airport.returnCity);
        }
        if (airport.isOneWay) {
          setActiveTab(0);
        } else if (airport.isOneWay === false) {
          setActiveTab(1);
        }
        return;
      }
      setDepartureCity(airport);
    },
    [setDepartureCity, setReturnCity],
  );

  const onSelectReturnCity = useCallback(
    (airport) => {
      // Tarih kontrolü
      if (airport?.departureDateRaw || airport?.returnDateRaw) {
        const now = new Date();

        if (
          airport.departureDateRaw &&
          !isBefore(airport.departureDateRaw, now)
        ) {
          setDepartureDate(new Date(airport.departureDateRaw));
        }

        if (airport.returnDateRaw && !isBefore(airport.returnDateRaw, now)) {
          setReturnDate(new Date(airport.returnDateRaw));
        }
      }

      if (airport?.returnCity) {
        setReturnCity(airport.returnCity);
        if (airport.departureCity) {
          setDepartureCity(airport.departureCity);
        }
        if (airport.isOneWay) {
          setActiveTab(0);
        } else if (airport.isOneWay === false) {
          setActiveTab(1);
        }
        return;
      }
      setReturnCity(airport);
    },
    [setReturnCity, setDepartureCity],
  );

  // Utility functions
  const formatDateForUrl = useCallback((date) => {
    return formatDate(date, { format: "YYYY-MM-DD" });
  }, []);

  const generateFlightLink = useCallback(() => {
    // const fromCode = state.departureCity?.code || "";
    // const toCode = state.returnCity?.code || "";
    const airportInfo = `${state.departureCity.id}-${state.returnCity.id}`;
    // const airportInfo = `${state.departureCity.id}-${state.returnCity.id}-${fromCode}-${toCode}`;
    const depDate = formatDateForUrl(state.departureDate);

    // Query parametreleri oluştur
    const queryParams = new URLSearchParams();

    // Mevcut URL'deki campaignId'yi kontrol et ve varsa ekle
    const currentParams = new URLSearchParams(window.location.search);
    const campaignId = currentParams.get("campaignId");
    if (campaignId) {
      queryParams.set("campaignId", campaignId);
    }

    // Yolcu bilgileri: adult her zaman (0 dahil) — liste sayfası adult yoksa varsayılan 1 sayıyordu
    queryParams.set("adult", String(state.passengers.adult));
    if (state.passengers.student > 0)
      queryParams.set("student", state.passengers.student.toString());
    if (state.passengers.child > 0)
      queryParams.set("child", state.passengers.child.toString());
    if (state.passengers.infant > 0)
      queryParams.set("infant", state.passengers.infant.toString());
    if (state.passengers.elderly > 0)
      queryParams.set("elderly", state.passengers.elderly.toString());

    // Kabin sınıfını ekle
    if (state.cabinClass && state.cabinClass !== "economy") {
      queryParams.set("class", state.cabinClass);
    }

    // Direct flights seçeneğini ekle
    if (state.onlyDirectFlights) {
      queryParams.set("directOnly", "true");
      queryParams.set("recommendations", "direct");
      queryParams.set("stops", "1");
    }

    const queryString = queryParams.toString();
    const baseUrl =
      state.activeTab === 0
        ? `/flight/one-way/${airportInfo}/${depDate}`
        : `/flight/round-trip/${airportInfo}/${depDate}/${formatDateForUrl(state.returnDate)}`;

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [
    state.departureCity,
    state.returnCity,
    state.departureDate,
    state.returnDate,
    state.activeTab,
    formatDateForUrl,
    state.passengers,
    state.cabinClass,
    state.onlyDirectFlights,
  ]);

  const getPassengerInfo = useCallback(() => {
    const adultCount = state.passengers.adult;
    const childCount = state.passengers.child;
    const infantCount = state.passengers.infant;

    // Bu fonksiyon tForm kullanıyor, o yüzden FlightSearchForm'da kalması daha iyi
    // Ama basic info string'i döndürebiliriz
    return { adultCount, childCount, infantCount };
  }, [state.passengers]);

  const getPassengerInfoText = useCallback(
    (t) => {
      const totalPassengers =
        state.passengers.adult +
        state.passengers.student +
        state.passengers.child +
        state.passengers.infant +
        state.passengers.elderly;

      return `${totalPassengers} ${t("common.passenger")}`;
    },
    [state.passengers],
  );

  // Validation
  const validateForm = useCallback(() => {
    const formData = {
      departureCity: state.departureCity,
      returnCity: state.returnCity,
      ...(state.activeTab === 1 && { returnDate: state.returnDate }),
      isRoundTrip: state.activeTab === 1,
    };

    try {
      flightSearchSchema.parse(formData);
      return {};
    } catch (error) {
      const errors = {};
      error.errors.forEach((err) => {
        errors[err.path[0]] = err.message;
      });
      return errors;
    }
  }, [
    state.departureCity,
    state.returnCity,
    state.activeTab,
    state.returnDate,
    flightSearchSchema,
  ]);

  const getPublicHolidays = async () => {
    try {
      const publicHolidaysResponse = await staticDataService.getStaticData({
        LanguageCode: locale.toUpperCase(),
        PageName: "PublicHolidays",
        GroupNames: [
          new Date().getMonth() === 11
            ? new Date().getFullYear() + 1
            : new Date().getFullYear(),
        ],
      });

      const publicHolidays = JSON.parse(
        publicHolidaysResponse?.ContentParts[0]?.ContentGroupKeys[0]
          ?.ContentGroupValues[0]?.Value,
      ).map((item) => ({
        date: item.date,
        title: item.localName,
      }));
      setPublicHolidays(publicHolidays);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "getPublicHolidays: Public holidays yüklenirken hata oluştu:",
        error,
      );
    }
  };

  const getFlightDefaultOrigin = async () => {
    if (initialData.initialDepartureCityCode) {
      return;
    }

    try {
      const flightDefaultOriginResponse =
        await flightService.getFlightDefaultOrigin({
          LanguageCode: locale.toUpperCase(),
        });

      const autoCompleteResult =
        flightDefaultOriginResponse?.OriginEntity?.AutoCompleteResult;
      const cityNameLowerCase = autoCompleteResult?.cityName?.toLowerCase();

      if (
        initialData?.initialReturnCityCode &&
        (autoCompleteResult?.id === initialData.initialReturnCityCode ||
          cityNameLowerCase === initialData.initialReturnCityCode ||
          autoCompleteResult?.code === initialData.initialReturnCityCode)
      ) {
        return;
      }

      setDepartureCity(
        flightDefaultOriginResponse?.OriginEntity?.AutoCompleteResult,
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "getFlightDefaultOrigin: Flight default origin yüklenirken hata oluştu:",
        error,
      );
    }
  };

  useEffect(() => {
    const latestFlightSearches = localStorage.getItem("latestFlightSearches");
    if (latestFlightSearches) {
      const parsedSearches = JSON.parse(latestFlightSearches);
      // Eğer 3'ten fazla arama varsa, son 3 tanesini al
      const limitedSearches =
        parsedSearches.length > 3 ? parsedSearches.slice(0, 3) : parsedSearches;
      setLatestFlightSearches(limitedSearches);
    }

    getPopularCities();
    getNearestAirports();
    getPublicHolidays();

    // Eğer initial state'de departureCity yoksa default origin'i getir
    if (!initialData.initialDepartureCityCode && locale) {
      getFlightDefaultOrigin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  // Initial data'da şehir kodları varsa API'den gerçek şehir bilgilerini getir
  useEffect(() => {
    const loadInitialCityData = async () => {
      try {
        // Departure city kodunu kontrol et
        if (initialData.initialDepartureCityCode && !state.departureCity) {
          const departureCityData = await getCityInfoByCode(
            initialData.initialDepartureCityCode,
          );
          if (departureCityData) {
            setDepartureCity(departureCityData);
          }
        }

        // Return city kodunu kontrol et
        if (initialData.initialReturnCityCode && !state.returnCity) {
          const returnCityData = await getCityInfoByCode(
            initialData.initialReturnCityCode,
          );
          if (returnCityData) {
            setReturnCity(returnCityData);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          "loadInitialCityData: Initial şehir bilgileri yüklenirken hata oluştu:",
          error,
        );
      }
    };

    // Sadece locale yüklendiyse çalıştır
    if (
      locale &&
      (initialData.initialDepartureCityCode ||
        initialData.initialReturnCityCode)
    ) {
      loadInitialCityData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locale,
    initialData.initialDepartureCityCode,
    initialData.initialReturnCityCode,
  ]);

  // Multi-city initial data'da şehir kodları varsa API'den gerçek şehir bilgilerini getir
  useEffect(() => {
    const loadMultiCityInitialData = async () => {
      if (!initialData.initialMultiFlights || !locale) return;

      try {
        setInitialLoading(true);

        const updatedFlights = [];

        for (const flight of initialData.initialMultiFlights) {
          const updatedFlight = { ...flight };

          // Departure city code varsa API'den getir
          if (flight.departureCityCode) {
            const departureCityData = await getCityInfoByCode(
              flight.departureCityCode,
            );
            if (departureCityData) {
              updatedFlight.departureCity = departureCityData;
            }
          }

          // Arrival city code varsa API'den getir
          if (flight.arrivalCityCode) {
            const arrivalCityData = await getCityInfoByCode(
              flight.arrivalCityCode,
            );
            if (arrivalCityData) {
              updatedFlight.arrivalCity = arrivalCityData;
            }
          }

          updatedFlights.push(updatedFlight);
        }

        // Tüm şehir bilgileri yüklendiyse multiFlights'ı güncelle
        if (updatedFlights.length > 0) {
          // Reducer'da multiFlights'ı güncellemek için SET_MULTI_FLIGHTS action'ını kullan
          dispatch({ type: "SET_MULTI_FLIGHTS", payload: updatedFlights });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          "loadMultiCityInitialData: Multi-city şehir bilgileri yüklenirken hata oluştu:",
          error,
        );
      } finally {
        setInitialLoading(false);
      }
    };

    loadMultiCityInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, initialData.initialMultiFlights]);

  // Currency ve locale yüklendiğinde cheapest flights'ı getir
  useEffect(() => {
    if (currency && state.departureCity && state.returnCity) {
      getCheapestFlightsMonthly();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, state.departureCity, state.returnCity, state.activeTab]);

  // departureDate değiştiğinde calendar prices'ı getir
  useEffect(() => {
    if (
      state.departureCity &&
      state.returnCity &&
      state.departureDate &&
      currency &&
      locale
    ) {
      if (state.activeTab === 1) {
        getRoundTripCalendarPrices();
      } else {
        getOneWayCalendarPrices();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.departureDate,
    state.departureCity,
    state.returnCity,
    currency,
    locale,
    state.activeTab,
  ]);

  return {
    loading: state.loading,
    initialLoading: state.initialLoading,
    onlyDirectFlights: state.onlyDirectFlights,
    setLoading,
    setOnlyDirectFlights,
    setActiveTab,
    activeTab: state.activeTab,
    setDepartureDate,
    setReturnDate,
    departureDate: state.departureDate,
    returnDate: state.returnDate,
    setDepartureCity,
    setReturnCity,
    departureCity: state.departureCity,
    returnCity: state.returnCity,
    toggleDepartureAndReturn,
    passengers: state.passengers,
    cabinClass: state.cabinClass,
    setPassengers,
    setCabinClass,
    setPassengersAndCabinClass,
    latestFlightSearches: state.latestFlightSearches,
    setLatestFlightSearches,
    getPopularCities,
    popularCities: state.popularCities,
    getNearestAirports,
    nearestAirports: state.nearestAirports,
    getCheapestFlightsMonthly,
    cheapestFlightsMonthly: state.cheapestFlightsMonthly,
    getCheapestFlightsMonthlyByMonth,
    cheapestFlightsMonthlyByMonth: state.cheapestFlightsMonthlyByMonth,
    getOneWayCalendarPrices,
    oneWayCalendarPrices: state.oneWayCalendarPrices,
    // Multi-city flights
    multiFlights: state.multiFlights,
    addMultiFlight,
    removeMultiFlight,
    updateMultiFlight,
    toggleMultiFlightCities,
    // New functions
    searchForFromCity,
    getCityInfoByCode,
    onSelectDepartureCity,
    onSelectReturnCity,
    formatDateForUrl,
    generateFlightLink,
    getPassengerInfo,
    getPassengerInfoText,
    validateForm,
    publicHolidays: state.publicHolidays,
    state,
  };
};
// const getOneWayCalendarPrices = async () => {
//   try {
//     // Bugünden itibaren tarih aralığını hesapla
//     const dateRange = calculateCalendarDateRange(state.departureDate);

//     // İlk 119 günlük request
//     const firstRequestParams = {
//       Origin: state.departureCity.code,
//       OriginIsCity: true,
//       Destination: state.returnCity.code,
//       DestinationIsCity: false,
//       DepartureDay: formatDate(dateRange.firstRequest.startDate, {
//         format: "YYYY-MM-DD",
//       }),
//       Currency: currency.toUpperCase(),
//       Language: locale.toUpperCase(),
//       NumberOfDays: dateRange.firstRequest.numberOfDays,
//     };

//     let firstResponse =
//       await flightService.getOneWayCalendarPrices(firstRequestParams);

//     // Eğer ek request gerekirse
//     let secondResponse = null;
//     if (dateRange.needsAdditionalRequest) {
//       const secondRequestParams = {
//         ...firstRequestParams,
//         DepartureDay: formatDate(dateRange.additionalRequest.startDate, {
//           format: "YYYY-MM-DD",
//         }),
//         NumberOfDays: dateRange.additionalRequest.numberOfDays,
//       };
//       secondResponse =
//         await flightService.getOneWayCalendarPrices(secondRequestParams);
//     }

//     // Response'ları birleştir ve işle
//     const combinedPrices = mergeCalendarPrices(
//       firstResponse?.DailyPrices || [],
//       secondResponse?.DailyPrices || null,
//     );

//     if (combinedPrices.length > 0) {
//       const startDate = dateRange.firstRequest.startDate;
//       const formattedDailyPrices = combinedPrices.map((price, index) => ({
//         ...price,
//         date: new Date(startDate.getTime() + index * 24 * 60 * 60 * 1000),
//       }));

//       setOneWayCalendarPrices(formattedDailyPrices);
//     } else {
//       setOneWayCalendarPrices(null);
//     }
//   } catch (error) {
//     console.error(
//       "getOneWayCalendarPrices: One way calendar prices yüklenirken hata oluştu:",
//       error,
//     );
//     setOneWayCalendarPrices(null);
//   }
// };

// const getRoundTripCalendarPrices = async () => {
//   try {
//     // Bugünden itibaren tarih aralığını hesapla
//     const dateRange = calculateCalendarDateRange(state.departureDate);

//     // İlk 119 günlük request
//     const firstRequestParams = {
//       Origin: state.departureCity.code,
//       OriginIsCity: true,
//       Destination: state.returnCity.code,
//       DestinationIsCity: false,
//       DepartureDay: formatDate(dateRange.firstRequest.startDate, {
//         format: "YYYY-MM-DD",
//       }),
//       ReturnDay: formatDate(state.returnDate, {
//         format: "YYYY-MM-DD",
//       }),
//       Currency: currency.toUpperCase(),
//       Language: locale.toUpperCase(),
//       NumberOfDays: dateRange.firstRequest.numberOfDays,
//     };

//     let firstResponse =
//       await flightService.getRoundTripCalendarPrices(firstRequestParams);

//     // Eğer ek request gerekirse
//     let secondResponse = null;
//     if (dateRange.needsAdditionalRequest) {
//       const secondRequestParams = {
//         ...firstRequestParams,
//         DepartureDay: formatDate(dateRange.additionalRequest.startDate, {
//           format: "YYYY-MM-DD",
//         }),
//         NumberOfDays: dateRange.additionalRequest.numberOfDays,
//       };
//       secondResponse =
//         await flightService.getRoundTripCalendarPrices(secondRequestParams);
//     }

//     // Response'ları birleştir ve işle
//     const combinedTotalPrices = mergeCalendarPrices(
//       firstResponse?.TotalCalculated?.DailyPrices || [],
//       secondResponse?.TotalCalculated?.DailyPrices || null,
//     );

//     if (combinedTotalPrices.length > 0) {
//       const startDate = dateRange.firstRequest.startDate;
//       const formattedDailyPrices = combinedTotalPrices.map(
//         (price, index) => ({
//           ...price,
//           date: new Date(startDate.getTime() + index * 24 * 60 * 60 * 1000),
//         }),
//       );

//       setOneWayCalendarPrices(formattedDailyPrices);
//     } else {
//       setOneWayCalendarPrices(null);
//     }
//   } catch (error) {
//     console.error(
//       "getRoundTripCalendarPrices: Round trip calendar prices yüklenirken hata oluştu:",
//       error,
//     );
//     setOneWayCalendarPrices(null);
//   }
// };
